import React, { useState, useEffect, useRef, useMemo } from 'react';
import round from 'lodash/round';
import { v4 } from 'uuid';
import Router$1, { useRouter, Router } from 'next/router';
import NProgress from 'nprogress';
import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock';
import ReactDOM from 'react-dom';
import * as t from 'typed-contracts';

const FETCH_STATUSES = {
  IDLE: "IDLE",
  LOADING: "LOADING",
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE"
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function isBrowser() {
  return typeof window !== "undefined";
}
function isServer() {
  return !isBrowser();
}
function canUseDOM() {
  return typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined";
}
function isResSent(res) {
  return res.finished || res.headersSent;
}
function isomorphicLog(message) {
  const isError = message instanceof Error;
  const log = isError ? console.error : console.log;
  if (isBrowser()) {
    const isProduction = process.env.NODE_ENV === "production";
    if (isProduction)
      return;
    if (isError) {
      log(message);
    } else {
      log(`%c ${message}`, "color: green");
    }
  } else {
    log(require("util").inspect(message, {
      colors: true
    }));
  }
}
function getNumberSign(value) {
  return value === 0 ? "" : value > 0 ? "+" : "-";
}
function formatNumber(value, options) {
  let result = typeof (options == null ? void 0 : options.precision) === "number" ? round(value, options.precision).toFixed(options.precision) : value.toString();
  result = result.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ").replace(".", ",");
  if (options == null ? void 0 : options.withSign) {
    result = getNumberSign(value) + result;
  }
  return result;
}
function generateNumberArray(length) {
  return Array.from({ length }, (_, index) => index);
}
function isStringGuard(value) {
  return typeof value === "string";
}
function isObjectGuard(value) {
  return typeof value === "object";
}
function isNonNullObjectGuard(value) {
  return isObjectGuard(value) && Boolean(value);
}
function isEnum(value, enumArray) {
  return enumArray.includes(value);
}
function isNotNullish(value) {
  return value !== null && value !== void 0;
}
function notFalsy(value) {
  return Boolean(value);
}
function convertSrcSet(sources) {
  return sources.slice(0, 2).map((url, index) => `${url} ${index + 1}x`).join(", ");
}
const DATA_URL_REGEX = /^data:([a-z]+\/[a-z0-9-+.]+(;[a-z0-9-.!#$%*+.{}|~`]+=[a-z0-9-.!#$%*+.{}|~`]+)*)?(;base64)?,([a-z0-9!$&',()*+;=\-._~:@/?%\s]*?)$/i;
function getImageTypeFromUrl(url) {
  var _a;
  if (!url)
    return null;
  const isValidDataUrl = DATA_URL_REGEX.test(url);
  if (isValidDataUrl) {
    const parts = url.trim().match(DATA_URL_REGEX);
    return parts ? (_a = parts[1]) != null ? _a : null : null;
  }
  const dotPositionIndex = url.lastIndexOf(".");
  if (dotPositionIndex === -1)
    return null;
  const extension = url.slice(dotPositionIndex + 1);
  switch (extension) {
    case "svg":
      return "image/svg+xml";
    case "webp":
      return "image/webp";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
  }
  return null;
}
function createResource(data, status, error) {
  return {
    data,
    status,
    error: error != null ? error : null,
    meta: null
  };
}
function createResourceLoader(initialData) {
  return {
    getInitialResource() {
      return createResource(initialData, FETCH_STATUSES.IDLE);
    },
    pending(data) {
      const resourceData = data === void 0 ? initialData : data;
      return createResource(resourceData, FETCH_STATUSES.LOADING);
    },
    fulfill(payload) {
      return createResource(payload, FETCH_STATUSES.SUCCESS);
    },
    reject(error) {
      return createResource(initialData, FETCH_STATUSES.FAILURE, error);
    },
    cancel() {
      return createResource(initialData, FETCH_STATUSES.IDLE);
    }
  };
}
function createPaginatedResourceLoader(initialData = []) {
  return {
    getInitialResource() {
      return {
        data: initialData,
        status: FETCH_STATUSES.IDLE,
        error: null,
        meta: null
      };
    },
    pending(data) {
      const resourceData = data === void 0 ? initialData : data;
      return {
        data: resourceData,
        status: FETCH_STATUSES.LOADING,
        error: null,
        meta: null
      };
    },
    fulfill(payload, meta) {
      return {
        data: payload,
        status: FETCH_STATUSES.SUCCESS,
        error: null,
        meta
      };
    },
    reject(error) {
      return {
        data: initialData,
        status: FETCH_STATUSES.FAILURE,
        error: error != null ? error : null,
        meta: null
      };
    },
    cancel() {
      return {
        data: initialData,
        status: FETCH_STATUSES.IDLE,
        error: null,
        meta: null
      };
    }
  };
}
function trimEndSlash(url) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}
function getOrigin() {
  var _a;
  return isBrowser() ? window.location.origin : trimEndSlash((_a = process.env.NEXT_PUBLIC_ORIGIN) != null ? _a : "");
}
function isAbsoluteUrl(url) {
  const PROTOCOL_LIST = [
    "ftp",
    "file",
    "http",
    "https",
    "ws",
    "wss",
    "tel",
    "mailto",
    "sms",
    "callto",
    "mms",
    "skype",
    "javascript",
    "data",
    "chrome"
  ];
  return PROTOCOL_LIST.some((protocol) => url.startsWith(protocol + ":"));
}
function getAbsoluteUrl(urlOrPath) {
  if (isAbsoluteUrl(urlOrPath))
    return urlOrPath;
  return getOrigin() + urlOrPath;
}
function parseUrl(absoluteUrl) {
  try {
    return new URL(absoluteUrl);
  } catch (error) {
    return null;
  }
}
function shouldGetResourceDataFromCache(resource, shouldInvalidate) {
  const isLoading = resource.status === FETCH_STATUSES.LOADING;
  const isInvalidatingNotNeeded = resource.status === FETCH_STATUSES.SUCCESS && !shouldInvalidate;
  return isInvalidatingNotNeeded || isLoading;
}
function appendScriptCodeToBody(scriptCode) {
  if (!canUseDOM())
    return;
  const script = document.createElement("script");
  script.text = scriptCode;
  document.body.appendChild(script);
}
function createUuid() {
  return v4();
}

function assignRef(ref, value) {
  if (ref == null)
    return;
  if (typeof ref === "function") {
    ref(value);
  } else {
    try {
      ref.current = value;
    } catch (error) {
      throw new Error(`Cannot assign value "${value}" to ref "${ref}"`);
    }
  }
}
function createNamedContext(name, defaultValue) {
  const Ctx = React.createContext(defaultValue);
  Ctx.displayName = name;
  return Ctx;
}
function createContextHookWithProvider(name, defaultValue) {
  const context = createNamedContext(name, defaultValue);
  function useCtx() {
    const ctx = React.useContext(context);
    if (!ctx)
      throw new Error("useCtx must be inside a Provider with a value");
    return ctx;
  }
  return [useCtx, context.Provider];
}
const useIsomorphicLayoutEffect = canUseDOM() ? React.useLayoutEffect : React.useEffect;

function useFixedVhProperty(options) {
  useIsomorphicLayoutEffect(() => {
    function updateVhProperty() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        });
      });
    }
    updateVhProperty();
    window.addEventListener("orientationchange", updateVhProperty);
    if (options == null ? void 0 : options.shouldListenResize) {
      window.addEventListener("resize", updateVhProperty);
    }
    return () => {
      window.removeEventListener("orientationchange", updateVhProperty);
      if (options == null ? void 0 : options.shouldListenResize) {
        window.removeEventListener("resize", updateVhProperty);
      }
    };
  }, []);
}

function useMedia(query, defaultState = false) {
  const [state, setState] = useState(defaultState);
  useIsomorphicLayoutEffect(() => {
    let mounted = true;
    const mql = window.matchMedia(query);
    const onChange = () => {
      if (!mounted) {
        return;
      }
      setState(mql.matches);
    };
    mql.addListener(onChange);
    setState(mql.matches);
    return () => {
      mounted = false;
      mql.removeListener(onChange);
    };
  }, [query]);
  return state;
}

function useOnClickOutside(ref, handler, type) {
  useEffect(() => {
    function listener(event) {
      if (type === "custom") {
        handler(event);
      } else {
        if (!ref || !ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      }
    }
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler, type]);
}

function useOnKeyDown(keys, callback) {
  const callbackRef = useRef(callback);
  const KEY_SEPARATOR = "|";
  const joinedKeys = Array.isArray(keys) ? keys.join(KEY_SEPARATOR) : keys;
  const memoizedKeys = useMemo(() => joinedKeys.split(KEY_SEPARATOR), [
    joinedKeys
  ]);
  callbackRef.current = callback;
  useEffect(() => {
    function listener(event) {
      if (memoizedKeys.includes(event.key)) {
        callbackRef.current(event);
      }
    }
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [memoizedKeys]);
}

function usePrevious(value) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

function parseNumberParam(param) {
  if (!param || !param.trim())
    return null;
  const number = Number(param);
  return Number.isNaN(number) ? null : number;
}
function parseDateParam(param) {
  if (!param || !param.trim())
    return null;
  const date = new Date(param);
  const isInvalid = Number.isNaN(date.valueOf());
  return isInvalid ? null : date;
}
function parseBooleanParam(value) {
  if (!value)
    return null;
  const lowerCaseValue = value.trim().toLowerCase();
  if (!["true", "false"].includes(lowerCaseValue))
    return null;
  return lowerCaseValue === "true";
}
function parseEnum(param, enumArray) {
  if (!param)
    return null;
  const foundValue = enumArray.find((enumValue) => enumValue.toLowerCase() === param.toLowerCase());
  return foundValue || null;
}
function parseAbsoluteUrl(absoluteUrl) {
  try {
    return new URL(absoluteUrl);
  } catch (error) {
    return null;
  }
}
function dividePathnameAndSearch(url) {
  const questionMarkIndex = url.indexOf("?");
  if (questionMarkIndex === -1) {
    return [url, ""];
  } else {
    const pathname = url.slice(0, questionMarkIndex);
    const search = url.slice(questionMarkIndex);
    return [pathname, search];
  }
}
function getSearchFromUrl(url) {
  const [, search] = dividePathnameAndSearch(url);
  return search;
}
function getSearchParamsFromUrl(url) {
  return new URLSearchParams(getSearchFromUrl(url));
}
function mergeSearchParamsIntoUrl(currentUrl, params) {
  const [pathname, search] = dividePathnameAndSearch(currentUrl);
  const searchParams = new URLSearchParams(search);
  if (params instanceof URLSearchParams) {
    params.forEach((key, value) => {
      searchParams.set(key, value);
    });
  } else {
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === null || value === void 0) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, String(value));
      }
    });
  }
  return [pathname, searchParams.toString()].filter(Boolean).join("?");
}
function convertParamsToString(params = {}) {
  let resultSearchParams;
  if (params instanceof URLSearchParams) {
    resultSearchParams = params;
  } else {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => searchParams.append(key, value));
    resultSearchParams = searchParams;
  }
  const queryString = resultSearchParams.toString();
  return queryString ? "?" + queryString : "";
}

function useSearchParams() {
  var _a;
  const router = useRouter();
  return getSearchParamsFromUrl((_a = router.asPath) != null ? _a : "");
}

function useUpdateEffect(effect, deps) {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      effect();
    } else {
      mounted.current = true;
    }
  }, deps);
}

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
function useProgressBar(options) {
  useEffect(() => {
    const defaultOptions = { showSpinner: false };
    NProgress.configure(__spreadValues$1(__spreadValues$1({}, defaultOptions), options));
    let timeoutId = null;
    const TIMEOUT = 500;
    function resetTimeoutIfNeeded() {
      if (timeoutId) {
        if (typeof timeoutId === "number") {
          clearTimeout(timeoutId);
        } else {
          clearTimeout(timeoutId);
        }
        timeoutId = null;
      }
    }
    Router.events.on("routeChangeStart", () => {
      resetTimeoutIfNeeded();
      timeoutId = setTimeout(() => NProgress.start(), TIMEOUT);
    });
    Router.events.on("routeChangeComplete", () => {
      resetTimeoutIfNeeded();
      NProgress.done();
    });
    Router.events.on("routeChangeError", () => {
      resetTimeoutIfNeeded();
      NProgress.done();
    });
  }, []);
}

class Scroller {
  constructor() {
    this.listeners = [];
    this.isLocked = false;
    this.scrollbarWidthBeforeLock = this.getCurrentScrollbarWidth();
  }
  setLocked(isLocked) {
    this.isLocked = isLocked;
    this.notifyListeners();
  }
  getCurrentScrollbarWidth() {
    if (isServer())
      return 0;
    return window.innerWidth - document.body.clientWidth;
  }
  getScrollbarWidthBeforeLock() {
    return this.isLocked ? this.scrollbarWidthBeforeLock : this.getCurrentScrollbarWidth();
  }
  lock(...args) {
    this.scrollbarWidthBeforeLock = this.getCurrentScrollbarWidth();
    lock(...args);
    this.setLocked(true);
  }
  unlock(...args) {
    unlock(...args);
    this.setLocked(false);
  }
  unlockAll() {
    clearBodyLocks();
    this.setLocked(false);
  }
  notifyListeners() {
    const event = {
      isLocked: this.isLocked,
      scrollbarWidthBeforeLock: this.scrollbarWidthBeforeLock,
      currentScrollbarWidth: this.getCurrentScrollbarWidth()
    };
    this.listeners.forEach((listener) => {
      listener(event);
    });
  }
  subscribe(newListener) {
    this.listeners.push(newListener);
    const unsubscribe = () => {
      this.listeners = this.listeners.filter((oldListener) => oldListener !== newListener);
    };
    return unsubscribe;
  }
}
const scroller = new Scroller();

function useScrollLock() {
  const [isLocked, setLocked] = useState(scroller.isLocked);
  useEffect(() => {
    const unsubscribe = scroller.subscribe((event) => {
      setLocked(event.isLocked);
      return unsubscribe;
    });
  }, []);
  return isLocked;
}

function useScrollLockForRef(elementRef) {
  useEffect(() => {
    const element = elementRef.current;
    if (!element)
      return;
    scroller.lock(element);
    return () => {
      scroller.unlock(element);
    };
  }, []);
}

/*! js-cookie v3.0.1 | MIT */
/* eslint-disable no-var */
function assign (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      target[key] = source[key];
    }
  }
  return target
}
/* eslint-enable no-var */

/* eslint-disable no-var */
var defaultConverter = {
  read: function (value) {
    if (value[0] === '"') {
      value = value.slice(1, -1);
    }
    return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent)
  },
  write: function (value) {
    return encodeURIComponent(value).replace(
      /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
      decodeURIComponent
    )
  }
};
/* eslint-enable no-var */

/* eslint-disable no-var */

function init (converter, defaultAttributes) {
  function set (key, value, attributes) {
    if (typeof document === 'undefined') {
      return
    }

    attributes = assign({}, defaultAttributes, attributes);

    if (typeof attributes.expires === 'number') {
      attributes.expires = new Date(Date.now() + attributes.expires * 864e5);
    }
    if (attributes.expires) {
      attributes.expires = attributes.expires.toUTCString();
    }

    key = encodeURIComponent(key)
      .replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent)
      .replace(/[()]/g, escape);

    var stringifiedAttributes = '';
    for (var attributeName in attributes) {
      if (!attributes[attributeName]) {
        continue
      }

      stringifiedAttributes += '; ' + attributeName;

      if (attributes[attributeName] === true) {
        continue
      }

      // Considers RFC 6265 section 5.2:
      // ...
      // 3.  If the remaining unparsed-attributes contains a %x3B (";")
      //     character:
      // Consume the characters of the unparsed-attributes up to,
      // not including, the first %x3B (";") character.
      // ...
      stringifiedAttributes += '=' + attributes[attributeName].split(';')[0];
    }

    return (document.cookie =
      key + '=' + converter.write(value, key) + stringifiedAttributes)
  }

  function get (key) {
    if (typeof document === 'undefined' || (arguments.length && !key)) {
      return
    }

    // To prevent the for loop in the first place assign an empty array
    // in case there are no cookies at all.
    var cookies = document.cookie ? document.cookie.split('; ') : [];
    var jar = {};
    for (var i = 0; i < cookies.length; i++) {
      var parts = cookies[i].split('=');
      var value = parts.slice(1).join('=');

      try {
        var foundKey = decodeURIComponent(parts[0]);
        jar[foundKey] = converter.read(value, foundKey);

        if (key === foundKey) {
          break
        }
      } catch (e) {}
    }

    return key ? jar[key] : jar
  }

  return Object.create(
    {
      set: set,
      get: get,
      remove: function (key, attributes) {
        set(
          key,
          '',
          assign({}, attributes, {
            expires: -1
          })
        );
      },
      withAttributes: function (attributes) {
        return init(this.converter, assign({}, this.attributes, attributes))
      },
      withConverter: function (converter) {
        return init(assign({}, this.converter, converter), this.attributes)
      }
    },
    {
      attributes: { value: Object.freeze(defaultAttributes) },
      converter: { value: Object.freeze(converter) }
    }
  )
}

var api$1 = init(defaultConverter, { path: '/' });

function getCookie(field, req) {
  var _a, _b, _c;
  if (isBrowser()) {
    return (_a = api$1.get(field)) != null ? _a : null;
  }
  if (req) {
    const cookie2 = require("cookie");
    const cookieMap = cookie2.parse((_b = req.headers.cookie) != null ? _b : "");
    return (_c = cookieMap[field]) != null ? _c : null;
  }
  return null;
}
function getSetCookieHeaders(res) {
  var _a;
  const headerValue = (_a = res.getHeader("Set-Cookie")) != null ? _a : [];
  const headerArray = Array.isArray(headerValue) ? headerValue : [headerValue];
  return headerArray.map(String);
}
function updateCookie(field, value, res, expiresInDays, domain) {
  if (isBrowser()) {
    api$1.set(field, value, {
      path: "/",
      expires: expiresInDays,
      domain
    });
  } else {
    if (res && !isResSent(res)) {
      const cookie2 = require("cookie");
      const newCookie = cookie2.serialize(field, value, domain ? { domain } : {});
      res.setHeader("Set-Cookie", [...getSetCookieHeaders(res), newCookie]);
    } else {
      if (!res) {
        console.error("Cannot update cookie, because response is empty");
      } else if (isResSent(res)) {
        console.warn("Cannot update cookie, because response headers are sent");
      }
    }
  }
}
function removeCookie(field, res, domain) {
  if (isBrowser()) {
    api$1.remove(field, {
      domain
    });
  } else {
    if (res && !isResSent(res)) {
      const cookie2 = require("cookie");
      const newCookie = cookie2.serialize(field, "", domain ? {
        expires: new Date(0),
        domain
      } : { expires: new Date(0) });
      res.setHeader("Set-Cookie", [...getSetCookieHeaders(res), newCookie]);
    } else {
      if (!res) {
        console.error("Cannot remove cookie, because response is empty");
      } else if (isResSent(res)) {
        console.warn("Cannot remove cookie, because response headers are sent");
      }
    }
  }
}
const cookie = {
  get: getCookie,
  set: updateCookie,
  remove: removeCookie
};

class RequestError extends Error {
  constructor(status, body) {
    super(JSON.stringify({ status, body }, null, 2));
    this.status = status;
    this.body = body;
    isomorphicLog(this);
  }
  asObject() {
    return {
      statusCode: this.status.code,
      errorMessage: this.body && typeof this.body === "object" && "message" in this.body ? this.body["message"] : this.status.text
    };
  }
}

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const IsomorphicFormData = isBrowser() ? FormData : require("form-data");
const HTTP_METHODS = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
  PATCH: "PATCH"
};
const ACCESS_TOKEN_COOKIE = "accessToken";
const REFRESH_TOKEN_COOKIE = "refreshToken";
const DEFAULT_CONFIG = {
  useRefreshToken: false,
  baseUrl: {
    csr: process.env.NEXT_PUBLIC_CSR_API_URL,
    ssr: process.env.NEXT_PUBLIC_SSR_API_URL
  },
  accessTokenCookieName: ACCESS_TOKEN_COOKIE,
  refreshTokenCookieName: REFRESH_TOKEN_COOKIE
};
class ApiService {
  constructor(config) {
    var _a, _b, _c;
    this.accessToken = null;
    this.refreshToken = null;
    this.refreshRequest = null;
    this.unauthorizedErrorHandler = null;
    this.config = config || DEFAULT_CONFIG;
    this.language = (_a = process.env.NEXT_PUBLIC_LANGUAGE) != null ? _a : null;
    this.accessTokenCookieName = (_b = this.config.accessTokenCookieName) != null ? _b : DEFAULT_CONFIG.accessTokenCookieName;
    this.refreshTokenCookieName = (_c = this.config.refreshTokenCookieName) != null ? _c : DEFAULT_CONFIG.refreshTokenCookieName;
    this.cookieDomain = this.config.cookieDomain;
  }
  setConfig(config) {
    this.config = __spreadValues(__spreadValues({}, this.config), config);
  }
  getBaseUrl() {
    var _a;
    const baseUrl = (_a = this.config.baseUrl) != null ? _a : DEFAULT_CONFIG.baseUrl;
    if (isNonNullObjectGuard(baseUrl)) {
      return isBrowser() ? baseUrl.csr : baseUrl.ssr;
    }
    return baseUrl;
  }
  getLanguage() {
    return this.language;
  }
  setLanguage(lang) {
    this.language = lang;
  }
  setUnauthorizedErrorHandler(unauthorizedErrorHandler) {
    this.unauthorizedErrorHandler = unauthorizedErrorHandler;
  }
  handleUnauthorizedError() {
    if (this.unauthorizedErrorHandler) {
      this.unauthorizedErrorHandler();
    }
  }
  setAccessTokenWithoutCookie(accessToken) {
    if (!this.accessTokenCookieName)
      return;
    this.accessToken = accessToken;
  }
  setAccessToken(accessToken, remember = true) {
    if (!this.accessTokenCookieName)
      return;
    if (isBrowser()) {
      if (accessToken !== null) {
        if (remember) {
          cookie.set(this.accessTokenCookieName, accessToken, void 0, 365, this.cookieDomain);
        } else {
          cookie.set(this.accessTokenCookieName, accessToken, void 0, void 0, this.cookieDomain);
        }
      } else {
        cookie.remove(this.accessTokenCookieName, void 0, this.cookieDomain);
      }
    } else {
      this.accessToken = accessToken;
    }
  }
  setRefreshToken(refreshToken, remember = true) {
    if (!this.refreshTokenCookieName)
      return;
    if (isBrowser()) {
      if (refreshToken !== null) {
        if (remember) {
          cookie.set(this.refreshTokenCookieName, refreshToken, void 0, 365, this.cookieDomain);
        } else {
          cookie.set(this.refreshTokenCookieName, refreshToken, void 0, void 0, this.cookieDomain);
        }
      } else {
        cookie.remove(this.refreshTokenCookieName, void 0, this.cookieDomain);
      }
    } else {
      this.refreshToken = refreshToken;
    }
  }
  getAccessToken() {
    if (!this.accessTokenCookieName)
      return null;
    if (isBrowser()) {
      return cookie.get(this.accessTokenCookieName);
    } else {
      return this.accessToken;
    }
  }
  getRefreshToken() {
    if (!this.refreshTokenCookieName)
      return null;
    if (isBrowser()) {
      return cookie.get(this.refreshTokenCookieName);
    } else {
      return this.refreshToken;
    }
  }
  configureHeaders(body) {
    const headers = new Headers();
    const isFormData = body instanceof IsomorphicFormData;
    if (!isFormData) {
      headers.append("Content-Type", "application/json");
    }
    const accessToken = this.getAccessToken();
    if (accessToken) {
      headers.append("Authorization", `Bearer ${accessToken}`);
    }
    if (this.language) {
      headers.append("Accept-Language", this.language);
    }
    headers.append("Accept", "application/json");
    return headers;
  }
  configureBody(body) {
    if (!body)
      return null;
    if (body instanceof IsomorphicFormData) {
      return body;
    }
    return JSON.stringify(body);
  }
  getRequestUrl(pathname = "", queryParams) {
    const baseUrl = this.getBaseUrl();
    const search = convertParamsToString(queryParams);
    return [baseUrl, pathname, search].filter(isNotNullish).join("");
  }
  configureOptions({
    method,
    body,
    fetchOptions
  }) {
    return __spreadValues({
      headers: this.configureHeaders(body),
      method,
      mode: "cors",
      body: this.configureBody(body)
    }, fetchOptions);
  }
  getContent(response) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.toLowerCase().startsWith("application/json")) {
      return response.json().catch((error) => {
        if (error instanceof SyntaxError) {
          return response.text();
        } else {
          isomorphicLog(`Unknown error while parsing response body: ${error.toString()}`);
          return error.toString();
        }
      });
    }
    return response.text();
  }
  handleErrors(response) {
    return this.getContent(response).then((content) => {
      if (response.ok) {
        return content;
      }
      return Promise.reject(new RequestError({
        code: response.status,
        text: response.statusText
      }, content));
    });
  }
  logRequest(request2, comment) {
    let formattedLog = `--> ${request2.method} ${request2.url}`;
    if (comment) {
      formattedLog = formattedLog + ` - ${comment}`;
    }
    isomorphicLog(formattedLog);
  }
  logResponse(res, req, options) {
    return __async(this, null, function* () {
      const endTime = Date.now();
      const requestDuration = endTime - options.startTime;
      const formattedLog = `<-- ${req.method} ${res.status} ${requestDuration}ms ${res.url}`;
      isomorphicLog(formattedLog);
      return res;
    });
  }
  makeRefreshRequest() {
    const url = this.getRequestUrl("/auth/user");
    const options = this.configureOptions({
      method: HTTP_METHODS.POST,
      body: {
        clientId: 1,
        grantType: "refresh_token",
        refreshToken: this.getRefreshToken()
      }
    });
    const request2 = new Request(url, options);
    this.logRequest(request2, "Refresh token");
    const middlewareOptions = {
      startTime: Date.now()
    };
    return fetch(request2).then((response) => this.logResponse(response, request2, middlewareOptions)).then((response) => this.handleErrors(response)).then((body) => {
      this.setAccessToken(body.data.accessToken, false);
      this.setRefreshToken(body.data.refreshToken);
      return true;
    }).catch((error) => {
      if (!(error instanceof RequestError)) {
        isomorphicLog(error);
      }
      this.setAccessToken(null);
      this.setRefreshToken(null);
      return false;
    }).finally(() => {
      this.refreshRequest = null;
    });
  }
  refreshMiddleware(response, request2) {
    return __async(this, null, function* () {
      if (response.status !== 401 || !this.getRefreshToken())
        return response;
      if (!this.refreshRequest) {
        this.refreshRequest = this.makeRefreshRequest();
      }
      const isSuccess = yield this.refreshRequest;
      if (isSuccess) {
        const newRequest = request2.clone();
        newRequest.headers.set("Authorization", `Bearer ${this.getAccessToken()}`);
        this.logRequest(newRequest);
        const middlewareOptions = {
          startTime: Date.now()
        };
        return fetch(newRequest).then((response2) => this.logResponse(response2, newRequest, middlewareOptions));
      } else {
        return response;
      }
    });
  }
  unauthorizedMiddleware(response) {
    return __async(this, null, function* () {
      if (response.status === 401) {
        this.handleUnauthorizedError();
      }
      return response;
    });
  }
  createRequest({
    method,
    path,
    body,
    params,
    absoluteUrl,
    fetchOptions
  }) {
    const url = absoluteUrl || this.getRequestUrl(path, params);
    const options = this.configureOptions({ method, body, fetchOptions });
    return new Request(url, options);
  }
  runResponseMiddlewares(response, request2, options) {
    const middlewareList = [
      this.logResponse.bind(this)
    ];
    if (this.config.useRefreshToken) {
      middlewareList.push(this.refreshMiddleware.bind(this));
    }
    middlewareList.push(this.unauthorizedMiddleware.bind(this));
    return middlewareList.reduce((promise, middleware) => promise.then((response2) => middleware(response2, request2, options)), Promise.resolve(response));
  }
  executeRequest(request2) {
    this.logRequest(request2);
    const middlewareOptions = {
      startTime: Date.now()
    };
    return fetch(request2).then((response) => this.runResponseMiddlewares(response, request2, middlewareOptions)).then(this.handleErrors.bind(this)).catch((e) => {
      if (e instanceof TypeError && e.message === "Failed to fetch") {
        throw new RequestError({
          code: 0,
          text: "Server is not available"
        }, {});
      }
      throw e;
    });
  }
  bindHttpMethodToRequest(method) {
    return (options) => {
      const request2 = this.createRequest(__spreadProps(__spreadValues({}, options), { method }));
      return this.executeRequest(request2);
    };
  }
  getRequest() {
    return {
      get: this.bindHttpMethodToRequest(HTTP_METHODS.GET),
      post: this.bindHttpMethodToRequest(HTTP_METHODS.POST),
      put: this.bindHttpMethodToRequest(HTTP_METHODS.PUT),
      delete: this.bindHttpMethodToRequest(HTTP_METHODS.DELETE),
      patch: this.bindHttpMethodToRequest(HTTP_METHODS.PATCH)
    };
  }
}
const api = new ApiService();
const request = api.getRequest();

class ShortCodeService {
  constructor(name, propNames) {
    this.name = name;
    this.propNames = propNames || [];
    this.propsMap = /* @__PURE__ */ new Map();
  }
  getShortCodeRegex() {
    if (this.propNames.length === 0) {
      return new RegExp(`\\[${this.name}\\]`, "gi");
    }
    const propsRegexPartItems = this.propNames.map((propName) => `(?<${propName}>${propName}=".*?")`);
    let propsRegexPart = "";
    propsRegexPartItems.forEach((propsRegexPartItem, ind) => {
      propsRegexPart += "(\\s" + (ind === 0 ? "*" : "") + propsRegexPartItem + ")*";
    });
    return new RegExp(`\\[${this.name}\\s(?<props>${propsRegexPart})\\]`, "gi");
  }
  getShortCodeMatches(content) {
    return content.match(this.getShortCodeRegex()) || [];
  }
  getPropsRegex() {
    const propNamesPart = this.propNames.join("|");
    return new RegExp(`(?:${propNamesPart})=".*?"`, "gi");
  }
  getShortCodeProps(shortCode) {
    const propsRegex = this.getPropsRegex();
    const propsMatches = shortCode.match(propsRegex) || [];
    const props = {};
    propsMatches.forEach((propMatch) => {
      const separator = "=";
      const index = propMatch.indexOf(separator);
      const propName = propMatch.slice(0, index);
      const propValue = propMatch.slice(index + separator.length);
      props[propName] = propValue.slice(1, -1);
    });
    return props;
  }
  getShortCodeElement(shortCode) {
    const props = this.getShortCodeProps(shortCode);
    const shortCodeUuid = createUuid();
    this.propsMap.set(shortCodeUuid, props);
    const propsAttrs = Object.entries(props).map(([propName, propValue]) => {
      return `data-prop-${propName}="${propValue}"`;
    }).join(" ");
    return `<div id="${shortCodeUuid}" data-short-code="${this.name}" ${propsAttrs}></div>`;
  }
  getShortCodePropsByUuid(uuid) {
    return this.propsMap.get(uuid) || null;
  }
  getShortCodePropsByElement(element) {
    const uuid = element.getAttribute("id");
    if (!uuid)
      return null;
    return this.getShortCodePropsByUuid(uuid);
  }
  replaceShortCodeByElement(content) {
    const shortCodes = this.getShortCodeMatches(content);
    let replacedContent = content;
    shortCodes.forEach((shortCode) => {
      const element = this.getShortCodeElement(shortCode);
      replacedContent = replacedContent.replace(shortCode, element);
    });
    return replacedContent;
  }
  queryShortCodeElements() {
    return document.querySelectorAll(`[data-short-code="${this.name}"]`);
  }
  renderComponent(ShortCodeComponent) {
    const elements = this.queryShortCodeElements();
    elements.forEach((container) => {
      const props = this.getShortCodePropsByElement(container);
      if (props) {
        const shortCodeReactElement = React.createElement(ShortCodeComponent, props);
        ReactDOM.render(shortCodeReactElement, container);
      }
    });
  }
}

function openSharePopup(url) {
  window.open(url, "", "toolbar=0,status=0,width=626,height=436");
}
function getOpenGraphParam(param) {
  var _a;
  const metaElement = document.querySelector('meta[property="og:' + param + '"]');
  return (_a = metaElement == null ? void 0 : metaElement.content) != null ? _a : "";
}
const shareHandlers = {
  vk: (params) => {
    const searchParams = new URLSearchParams();
    searchParams.set("url", params.url);
    searchParams.set("title", params.title);
    searchParams.set("description", params.description);
    searchParams.set("image", params.image);
    searchParams.set("noparse", "true");
    openSharePopup("http://vkontakte.ru/share.php?" + searchParams.toString());
  },
  facebook: (params) => {
    const searchParams = new URLSearchParams();
    searchParams.set("u", params.url);
    openSharePopup("https://www.facebook.com/sharer/sharer.php?" + searchParams.toString());
  }
};
function share(socialType) {
  if (socialType === "facebook") {
    shareHandlers.facebook({ url: document.location.href });
  }
  if (socialType === "vk") {
    shareHandlers.vk({
      url: document.location.href,
      title: getOpenGraphParam("title"),
      description: getOpenGraphParam("description"),
      image: getOpenGraphParam("image")
    });
  }
}

const NEW_LINE_REGEXP = /(\r\n|\r|\n)/g;
function nl2br(text) {
  if (!text)
    return null;
  return text.split(NEW_LINE_REGEXP).map((line, index) => {
    if (line.match(NEW_LINE_REGEXP)) {
      return React.createElement("br", { key: index });
    }
    return line;
  });
}

function redirect(params) {
  const destinationLocation = params.withQuery ? [params.location, getSearchParamsFromUrl(params.currentUrl).toString()].filter(Boolean).join("?") : params.location;
  if (params.res && !isResSent(params.res)) {
    params.res.setHeader("Content-Type", "text/html; charset=utf-8");
    params.res.setHeader("Location", destinationLocation);
    params.res.statusCode = 302;
    params.res.end();
  } else {
    Router$1.replace(destinationLocation);
  }
}

const EMAIL_REGEXP = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const validators = {
  required(value) {
    return !value.trim();
  },
  email(value) {
    return !EMAIL_REGEXP.test(value);
  }
};
const requestErrorBodyContract = t.object({
  errors: t.objectOf(t.object({
    code: t.string,
    message: t.string
  }))
});
const requestErrorBodySimpleContract = t.object({
  errors: t.objectOf(t.string)
});
function convertRequestErrorToMap(error) {
  if (error instanceof RequestError) {
    if (!error.body) {
      return {};
    }
    const validationResult = requestErrorBodyContract("RequestError.body", error.body);
    if (!(validationResult instanceof t.ValidationError)) {
      return Object.keys(validationResult.errors).reduce((result, key) => {
        var _a;
        result[key] = ((_a = validationResult.errors[key]) == null ? void 0 : _a.message) || "";
        return result;
      }, {});
    }
    const validationSimpleResult = requestErrorBodySimpleContract("RequestError.body", error.body);
    if (!(validationSimpleResult instanceof t.ValidationError)) {
      return Object.keys(validationSimpleResult.errors).reduce((result, key) => {
        result[key] = validationSimpleResult.errors[key] || "";
        return result;
      }, {});
    }
    return {};
  }
  return {};
}

export { ACCESS_TOKEN_COOKIE, ApiService, EMAIL_REGEXP, FETCH_STATUSES, REFRESH_TOKEN_COOKIE, RequestError, ShortCodeService, api, appendScriptCodeToBody, assignRef, canUseDOM, convertParamsToString, convertRequestErrorToMap, convertSrcSet, cookie, createContextHookWithProvider, createNamedContext, createPaginatedResourceLoader, createResource, createResourceLoader, createUuid, dividePathnameAndSearch, formatNumber, generateNumberArray, getAbsoluteUrl, getImageTypeFromUrl, getNumberSign, getOrigin, getSearchFromUrl, getSearchParamsFromUrl, isAbsoluteUrl, isBrowser, isEnum, isNonNullObjectGuard, isNotNullish, isObjectGuard, isResSent, isServer, isStringGuard, isomorphicLog, mergeSearchParamsIntoUrl, nl2br, notFalsy, parseAbsoluteUrl, parseBooleanParam, parseDateParam, parseEnum, parseNumberParam, parseUrl, redirect, request, scroller, share, shouldGetResourceDataFromCache, trimEndSlash, useDebounce, useFixedVhProperty, useIsomorphicLayoutEffect, useMedia, useOnClickOutside, useOnKeyDown, usePrevious, useProgressBar, useScrollLock, useScrollLockForRef, useSearchParams, useUpdateEffect, validators };
